import { useLazyQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { FinanceInsurancePerformanceReport } from '@trnsact/trnsact-shared-types';
import { useNotifications } from 'modules/notification';
import { dateRangeToMoments } from 'modules/finance/sharedFilters';
import { FINANCE_INSURANCE_PERFORMANCE_REPORT } from '../api';
import { PerformanceEnrichedRow, FinanceDateRange, PerformanceFiltersState, PerformanceSortKey } from '../types';

const SORT_KEY_MAP: Partial<Record<PerformanceSortKey, string>> = {
  avgProfitPerAllDeals: 'avgProfitPerDeal',
  avgProfitPerFIDeals: 'avgProfitPerFiDeals',
};

export function useFinancePerformanceData(
  filters: PerformanceFiltersState,
  sorting: { key: PerformanceSortKey; value: 'asc' | 'desc' }
) {
  const { showNotification } = useNotifications();
  const { key: sortKey, value: sortDir } = sorting;

  const [fetchReport, { data, loading }] = useLazyQuery<{
    financeInsurancePerformanceReport: FinanceInsurancePerformanceReport;
  }>(FINANCE_INSURANCE_PERFORMANCE_REPORT, {
    notifyOnNetworkStatusChange: true,
    onError: () => showNotification('Failed to load performance data', { type: 'error' }),
  });

  const input = useMemo(() => {
    const { from, to } = dateRangeToMoments(filters.dateRange, filters.customDateRange);
    return {
      groupBy: filters.groupBy,
      filters: {
        date: {
          range: {
            from: from.toISOString(),
            to: to.toISOString(),
          },
          by: filters.dateType,
        },
        ...(filters.paymentType !== 'ALL' && { paymentType: filters.paymentType }),
        ...(filters.dealStatus !== 'ALL' && { dealStatus: filters.dealStatus }),
      },
      sorting: {
        key: SORT_KEY_MAP[sortKey] ?? sortKey,
        value: sortDir,
      },
    };
  }, [filters, sortKey, sortDir]);

  useEffect(() => {
    fetchReport({ variables: { input } });
  }, [input, fetchReport]);

  const rows = useMemo((): PerformanceEnrichedRow[] => {
    const apiRows = data?.financeInsurancePerformanceReport?.rows ?? [];
    return apiRows.map(row => ({
      name: row.name,
      deals: row.deals,
      fiDeals: row.fiDeals,
      financePenetration: row.financePenetration,
      escPenetration: row.escPenetration,
      insurancePenetration: row.insurancePenetration,
      gapPenetration: row.gapPenetration,
      roadsidePenetration: row.roadsidePenetration,
      financeProfit: row.financeProfit,
      escProfit: row.escProfit,
      insuranceProfit: row.insuranceProfit,
      gapProfit: row.gapProfit,
      roadsideProfit: row.roadsideProfit,
      totalProfit: row.totalProfit,
      avgProfitPerAllDeals: row.avgProfitPerDeal,
      avgProfitPerFIDeals: row.avgProfitPerFiDeals,
    }));
  }, [data]);

  return { rows, loading };
}
